import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Banknote, Landmark, CheckCircle, Loader2, AlertCircle, Info, Wallet } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function PayoutManager() {
  const { driver_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [driver, setDriver] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    payout_method: 'bank_transfer',
    description: '',
  });

  const fetchDriverData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/wallets/${driver_id}`);
      const payload = data.data || data;
      const driverData = payload.wallet || payload;
      setDriver(driverData);
      const initialAmount = parseFloat(driverData.current_balance) > 0 ? driverData.current_balance : '';
      setFormData((prev) => ({
        ...prev,
        amount: initialAmount,
        description: `Liquidação total de saldo - ${driverData.first_name || 'Driver'}`,
      }));
    } catch (err) {
      console.error(err);
      setError('Falha ao sincronizar dados da carteira do motorista.');
    } finally {
      setLoading(false);
    }
  }, [driver_id]);

  useEffect(() => { fetchDriverData(); }, [fetchDriverData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payoutAmount = parseFloat(formData.amount);
    const currentBalance = parseFloat(driver?.current_balance || 0);

    if (isNaN(payoutAmount) || payoutAmount <= 0) {
      setError('O valor do pagamento deve ser superior a zero.');
      return;
    }
    if (payoutAmount > currentBalance) {
      setError(`Margem insuficiente. O saldo máximo para liquidação é ${currentBalance.toFixed(2)}€.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post('/payouts', {
        driver_id,
        amount: payoutAmount,
        payout_method: formData.payout_method,
        description: formData.description,
      });
      if (data.success) {
        toast.success('Liquidação Concluída', { description: 'O pagamento foi registado e o saldo atualizado no Ledger.' });
        navigate(`/finance/wallet/${driver_id}`);
      } else {
        throw new Error(data.error || 'Erro no pagamento');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro crítico no protocolo de Payout.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-80 flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">A validar snapshot financeiro...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-2 md:p-4">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 px-0">
        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao Extrato
      </Button>

      <Card>
        <CardHeader className="bg-slate-900 text-primary-foreground">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Wallet className="h-6 w-6" /> Executar Liquidação
          </CardTitle>
          <p className="text-xs uppercase tracking-wide text-primary-foreground/70">
            {driver?.first_name} {driver?.last_name}
          </p>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Valor Nominal (EUR)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-muted-foreground">€</span>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="pl-10 text-2xl font-bold"
                  required
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Info className="h-4 w-4" />
                <span>Saldo disponível: <strong>{parseFloat(driver?.current_balance || 0).toFixed(2)}€</strong></span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Canal de Distribuição</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payout_method: 'bank_transfer' })}
                  className={`flex flex-col items-center gap-2 rounded-xl border-2 p-5 text-sm font-semibold transition-all ${
                    formData.payout_method === 'bank_transfer'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Landmark className="h-6 w-6" />
                  <span className="uppercase tracking-wide">Transferência</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, payout_method: 'cash' })}
                  className={`flex flex-col items-center gap-2 rounded-xl border-2 p-5 text-sm font-semibold transition-all ${
                    formData.payout_method === 'cash'
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-600'
                      : 'border-border text-muted-foreground hover:border-emerald-600/50'
                  }`}
                >
                  <Banknote className="h-6 w-6" />
                  <span className="uppercase tracking-wide">Numerário</span>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Nota de Auditoria</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full rounded-md border border-input bg-transparent p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Descreva o motivo ou período desta liquidação..."
                rows={3}
              />
            </div>

            {error && (
              <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span className="text-sm font-semibold">{error}</span>
              </div>
            )}

            <Button type="submit" disabled={submitting || !formData.amount} className="w-full" size="lg">
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Consolidando Transação...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" /> Validar e Liquidar
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
